import { Injectable } from '@angular/core';
import {
  AddonHookValidationRules,
  ProposalsHooksService,
} from '../proposals-hooks.service';
import {
  ApplicationConfig,
  ApplicationData,
  ApplicationHooksData,
  Partners,
  Proposal,
} from '@lockton-apps/shared/interfaces';
import { FormGroup } from '@angular/forms';
import { ConstructionProductConfigId } from '@lockton-apps/shared/util/constants';

export interface ConstructionHooksData {}

@Injectable()
export class ConstructionApplicationHooksService extends ProposalsHooksService<ConstructionHooksData> {
  private static constructionQuestionnaireTriggers: string[] = [
    `fees.sourceOfFees.distribution['FY-1'].quantitySurveying`,
    `fees.sourceOfFees.distribution['FY-0'].quantitySurveying`,
    `fees.sourceOfFees.distribution['FY-1'].projectCoOrdination`,
    `fees.sourceOfFees.distribution['FY-0'].projectCoOrdination`,
    `fees.sourceOfFees.distribution['FY-1'].projectManagement`,
    `fees.sourceOfFees.distribution['FY-0'].projectManagement`,
  ];
  private static surveyAndValuingQuestionnaireTriggers: string[] = [
    `fees.sourceOfFees.distribution['FY-0'].surveyingAndValuing.residential`,
    `fees.sourceOfFees.distribution['FY-1'].surveyingAndValuing.residential`,
    `fees.sourceOfFees.distribution['FY-0'].surveyingAndValuing.commercial`,
    `fees.sourceOfFees.distribution['FY-1'].surveyingAndValuing.commercial`,
  ];

  override data: ApplicationHooksData = {
    deactivateDialog: this.deactivateDialogData,
  };

  override addonHookValidationRules: AddonHookValidationRules = {
    [`${Partners.CONSTRUCTION}-${ConstructionProductConfigId.ArchitectsAndEngineers}`]:
      {
        basements: {
          anyOf: [
            'areasOfPractice.lastFinancialYearWork.distribution.basementWork',
          ],
        },
        swimmingPools: {
          anyOf: [
            'areasOfPractice.lastFinancialYearWork.distribution.swimmingPools',
          ],
        },
        fireSafety: {
          anyOf: ['areasOfPractice.claddingAndFireSafety.required'],
        },
        cladding: {
          anyOf: ['areasOfPractice.claddingAndFireSafety.required'],
        },
      },
    [`${Partners.CONSTRUCTION}-${ConstructionProductConfigId.DesignAndConstruction}`]:
      {
        basements: {
          anyOf: [
            'activities.projectSplit.distribution.basements.new',
            'activities.projectSplit.distribution.basements.extension',
            'activities.projectSplit.distribution.basements.nonStructural',
          ],
        },
        swimmingPools: {
          anyOf: ['activities.activitySplit.distribution.swimmingPools'],
        },
        cladding: {
          anyOf: ['activities.projectSplit.distribution.cladding'],
        },
      },
    [`${Partners.CONSTRUCTION}-${ConstructionProductConfigId.Retail}`]: {
      basements: {
        anyOf:
          ConstructionApplicationHooksService.constructionQuestionnaireTriggers,
      },
      swimmingPools: {
        anyOf:
          ConstructionApplicationHooksService.constructionQuestionnaireTriggers,
      },
      fireSafety: {
        anyOf:
          ConstructionApplicationHooksService.constructionQuestionnaireTriggers,
      },
      cladding: {
        anyOf:
          ConstructionApplicationHooksService.constructionQuestionnaireTriggers,
      },
      surveyAndValuing: {
        anyOf:
          ConstructionApplicationHooksService.surveyAndValuingQuestionnaireTriggers,
      },
    },
    [`${Partners.CONSTRUCTION}-${ConstructionProductConfigId.MidMarketNonTSA}`]:
      {
        basements: {
          anyOf: [
            'areasOfPractice.projectSplit.distribution.basements.new',
            'areasOfPractice.projectSplit.distribution.basements.extension',
            'areasOfPractice.projectSplit.distribution.basements.nonStructural',
          ],
        },
        cladding: {
          anyOf: [
            'areasOfPractice.projectSplit.distribution.cladding',
            'areasOfPractice.cladding.highRiseProjects',
            'areasOfPractice.cladding.specialBuildingsResponsibility',
          ],
        },
        surveyAndValuing: {
          anyOf: [
            'areasOfPractice.activitySplit.distribution.surveyAndValuationWork.residential',
            'areasOfPractice.activitySplit.distribution.surveyAndValuationWork.commercial',
          ],
        },
      },
  };

  async beforeSave(
    data: ApplicationData,
    form: FormGroup,
    proposal: Proposal,
    config: ApplicationConfig
  ): Promise<Proposal> {
    return super.handleDynamicSteps(data, proposal, config);
  }

  async beforeStepSubmit(
    data: ApplicationData,
    form: FormGroup,
    proposal: Proposal,
    config: ApplicationConfig
  ): Promise<Proposal> {
    return super.handleDynamicSteps(data, proposal, config);
  }
}
